import { styled } from '@material-ui/styles';
import { MapContextState } from 'Plugins/FloorPlan/Context/MapContext';
import { IOpening } from 'Plugins/FloorPlan/Models/IOpening';
import { IRoom, RoomType } from 'Plugins/FloorPlan/Models/IRoom';
import { IRoomItem } from 'Plugins/FloorPlan/Models/IRoomItem';
import { PointUtils } from 'Plugins/FloorPlan/Utils/PointUtils';
import { CoordPoint, Wall } from 'Plugins/FloorPlan/Utils/Types';
import React, { CSSProperties } from 'react';
import { ThemeRes } from 'Web/Themes/ThemeRes';

export type ExportMapSvgProps = {
    style?: CSSProperties;
    mapState: Partial<MapContextState>;
};

export type ExportMapViewProps = {
    tagId?: string;
    type?: RoomType;
    walls?: Array<Wall>;
    rooms?: Array<IRoom>;
    roomItems?: Array<IRoomItem>;
    openings?: Array<IOpening>;
    showLegend?: boolean;
    legendPosition?: CoordPoint;
    rotationAngle?: number;
    style?: CSSProperties;
};

//#region  //* LEGEND

type MapLegendProps = { position?: CoordPoint; style?: CSSProperties };
export const MapLegend = ({ position, style, children }: React.PropsWithChildren<MapLegendProps>) => {
    return !children ? null : (
        <g id="Legend" style={style}>
            <LegendText
                id="LegendInfo"
                x={position?.x}
                y={position?.y}
                fontFamily={ThemeRes.Fonts.fontFamilyNunitoSans}>
                {children}
            </LegendText>
        </g>
    );
};

const LegendText = styled('text')({ fontSize: 8, fill: '#414447', textAnchor: 'start' });

//#endregion

export const toShrinkScale = (width: number) => {
    if (width < 500) {
        return 4;
    }
    if (width < 800) {
        return 2;
    }
    return 1;
};

export const exportWidth = 1080;

export const calculateMapBox = (
    map: { walls: Array<Wall>; roomItems: Array<IRoomItem> },
    rotationAngle: number = 0
) => {
    const { walls = [], roomItems = [] } = map;
    const points = [
        ...walls.map((x) => x.start),
        ...roomItems.reduce<Array<CoordPoint>>((items, x) => [...items, ...(x.coordsReal || [])], []),
    ];

    const overallBBox = PointUtils.calculateBoundingBox({ points: points })!;
    if (!overallBBox) {
        return null;
    }

    const legendHeight = 50;
    const margin = rotationAngle !== 0 ? 300 : 100;
    const boxMargin = margin / 5;
    const minWidth = 480;

    const xMin = overallBBox.xMin - boxMargin;
    const yMin = overallBBox.yMin - boxMargin;
    const xMax = overallBBox.xMax + boxMargin;
    const yMax = overallBBox.yMax + boxMargin + legendHeight;
    const height = yMax - yMin + legendHeight;
    const width = Math.max(xMax - xMin, minWidth);

    const legendPosition = { x: xMin + boxMargin, y: yMin + height - boxMargin };

    return { xMin, yMin, xMax, yMax, height, width, boxMargin, legendPosition };
};
